<template>
  <vs-popup
    class="clone-product-order-popup"
    id="clone-product-order-popup"
    :title="`CLONE SALES ORDER (${product_order_code})`"
    @close="open = false"
    :active.sync="open"
    :buttons-hidden="true">
    <vx-card no-shadow>
      <ValidationObserver ref="form" v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(onClickCloneOrder)" autocomplete="off">
          <div class="vx-row mb-4">
            <div class="vx-col w-full">
              <ValidationProvider vid="customers_order_number" name="Customer PO Number" rules="" v-slot="{ errors }">
                <small class="label">Customer PO Number</small>
                <vs-input
                  v-model="form.customers_order_number"
                  name="customers_order_number"
                  placeholder="Enter Customer PO Number"
                  class="w-full"
                  :danger="!!errors[0]"
                  :danger-text="errors[0]"
                  val-icon-danger="icon-x"
                  val-icon-pack="feather"
                />
              </ValidationProvider>
            </div>
          </div>

          <div class="vx-row mb-4">
            <div class="vx-col w-full">
              <h5 class="mb-2">Order Items</h5>
              <vs-table :data="form.formulation_items" class="table-dark-inverted">
                <template slot="thead">
                  <vs-th>PRODUCT / CODE</vs-th>
                  <vs-th>QUANTITY (KG)</vs-th>
                  <vs-th>PACK SIZE (KG)</vs-th>
                  <vs-th>ITEM STATUS</vs-th>
                  <vs-th>REQUIRED BY</vs-th>
                  <vs-th>SHIPMENT DATE</vs-th>
                  <vs-th class="text-center">ACTION</vs-th>
                </template>
                <template slot-scope="{data}">
                  <vs-tr :key="tr.id || indextr" v-for="(tr, indextr) in data">
                    <vs-td>
                      <span class="font-semibold">{{ tr.formulation_code }}</span>
                      <br v-if="tr.formulation_name">
                      <small class="text-muted" v-if="tr.formulation_name">{{ tr.formulation_name }}</small>
                    </vs-td>
                    <vs-td>
                      {{ tr.quantity }}
                    </vs-td>
                    <vs-td>
                      {{ tr.pack_size }}
                    </vs-td>
                    <vs-td>
                      <vs-chip transparent :color="itemStatusColorFactory(tr.item_status)">
                        {{ tr.item_status === 'Pending' ? 'Accepted' : (tr.item_status || 'N/A') }}
                      </vs-chip>
                    </vs-td>
                    <vs-td>
                      <ValidationProvider
                        :vid="`${indextr}_required_by`"
                        :name="`${indextr}_required_by`"
                        rules="required"
                        v-slot="{ errors }">
                        <flat-pickr
                          :config="dateConfig"
                          class="w-full"
                          :name="`${indextr}_required_by`"
                          v-model="tr.required_by"
                          placeholder="Choose Date"
                        />
                        <span class="text-danger text-sm" v-if="errors[0]">{{ errors[0] }}</span>
                      </ValidationProvider>
                    </vs-td>
                    <vs-td>
                      <ValidationProvider
                        :vid="`${indextr}_shipment_date`"
                        :name="`${indextr}_shipment_date`"
                        rules="required"
                        v-slot="{ errors }">
                        <flat-pickr
                          :config="dateConfig"
                          class="w-full"
                          :name="`${indextr}_shipment_date`"
                          v-model="tr.shipment_date"
                          placeholder="Choose Date"
                        />
                        <span class="text-danger text-sm" v-if="errors[0]">{{ errors[0] }}</span>
                      </ValidationProvider>
                    </vs-td>
                    <vs-td class="text-center">
                      <vx-tooltip text="Remove item" position="top">
                        <vs-button
                          size="small"
                          color="danger"
                          type="border"
                          icon-pack="feather"
                          icon="icon-trash-2"
                          @click="removeItem(indextr)"
                        />
                      </vx-tooltip>
                    </vs-td>
                  </vs-tr>
                </template>
              </vs-table>
              <div v-if="form.formulation_items.length === 0" class="text-center p-4 border border-solid border-grey-light rounded mt-2">
                <span class="text-danger text-sm">No items in the order. Please restore items from below or cancel.</span>
              </div>
            </div>
          </div>

          <!-- Removed Items Temporary Storage -->
          <div class="vx-row mb-4" v-if="removed_items.length > 0">
            <div class="vx-col w-full">
              <div class="flex items-center justify-between mb-2">
                <h5 class="text-warning flex items-center">
                  <feather-icon icon="ArchiveIcon" svgClasses="w-4 h-4 mr-2 text-warning" />
                  Removed Items ({{ removed_items.length }})
                </h5>
                <vs-button
                  size="small"
                  type="border"
                  color="warning"
                  @click="restoreAllItems">
                  Restore All
                </vs-button>
              </div>
              <vs-table :data="removed_items" class="table-dark-inverted">
                <template slot="thead">
                  <vs-th>PRODUCT / CODE</vs-th>
                  <vs-th>QUANTITY (KG)</vs-th>
                  <vs-th>PACK SIZE (KG)</vs-th>
                  <vs-th>ITEM STATUS</vs-th>
                  <vs-th class="text-center">ACTION</vs-th>
                </template>
                <template slot-scope="{data}">
                  <vs-tr :key="tr.id || indextr" v-for="(tr, indextr) in data">
                    <vs-td>
                      <span class="font-semibold">{{ tr.formulation_code }}</span>
                      <br v-if="tr.formulation_name">
                      <small class="text-muted" v-if="tr.formulation_name">{{ tr.formulation_name }}</small>
                    </vs-td>
                    <vs-td>
                      {{ tr.quantity }}
                    </vs-td>
                    <vs-td>
                      {{ tr.pack_size }}
                    </vs-td>
                    <vs-td>
                      <vs-chip transparent :color="itemStatusColorFactory(tr.item_status)">
                        {{ tr.item_status === 'Pending' ? 'Accepted' : (tr.item_status || 'N/A') }}
                      </vs-chip>
                    </vs-td>
                    <vs-td class="text-center">
                      <vx-tooltip text="Restore item to order" position="top">
                        <vs-button
                          size="small"
                          color="primary"
                          type="border"
                          icon-pack="feather"
                          icon="icon-plus"
                          @click="restoreItem(indextr)">
                          Add Back
                        </vs-button>
                      </vx-tooltip>
                    </vs-td>
                  </vs-tr>
                </template>
              </vs-table>
            </div>
          </div>

          <!-- Additional Costs -->
          <div class="vx-row mb-4" v-if="(form.add_costs && form.add_costs.length > 0) || (removed_add_costs && removed_add_costs.length > 0)">
            <div class="vx-col w-full">
              <h5 class="mb-2">Additional Costs</h5>
              <vs-table :data="form.add_costs" class="table-dark-inverted">
                <template slot="thead">
                  <vs-th>NAME / DESCRIPTION</vs-th>
                  <vs-th>NOMINAL CODE</vs-th>
                  <vs-th>AMOUNT (EXCL VAT)</vs-th>
                  <vs-th>VAT (%)</vs-th>
                  <vs-th>AMOUNT (INCL VAT)</vs-th>
                  <vs-th class="text-center">ACTION</vs-th>
                </template>
                <template slot-scope="{data}">
                  <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                    <vs-td>
                      <span class="font-semibold">{{ tr.name }}</span>
                    </vs-td>
                    <vs-td>
                      {{ getNominalCodeDisplay(tr) }}
                    </vs-td>
                    <vs-td>
                      {{ tr.amount }}
                    </vs-td>
                    <vs-td>
                      {{ tr.vat_percent }}%
                    </vs-td>
                    <vs-td>
                      {{ calculateTotalWithVat(tr.amount, tr.vat_percent) }}
                    </vs-td>
                    <vs-td class="text-center">
                      <vx-tooltip text="Remove cost" position="top">
                        <vs-button
                          size="small"
                          color="danger"
                          type="border"
                          icon-pack="feather"
                          icon="icon-trash-2"
                          @click="removeAddCost(indextr)"
                        />
                      </vx-tooltip>
                    </vs-td>
                  </vs-tr>
                </template>
              </vs-table>
              <div v-if="form.add_costs.length === 0" class="text-center p-4 border border-solid border-grey-light rounded mt-2">
                <span class="text-muted text-sm">All additional costs removed.</span>
              </div>
            </div>
          </div>

          <!-- Removed Additional Costs Temporary Storage -->
          <div class="vx-row mb-4" v-if="removed_add_costs.length > 0">
            <div class="vx-col w-full">
              <div class="flex items-center justify-between mb-2">
                <h5 class="text-warning flex items-center">
                  <feather-icon icon="ArchiveIcon" svgClasses="w-4 h-4 mr-2 text-warning" />
                  Removed Additional Costs ({{ removed_add_costs.length }})
                </h5>
                <vs-button
                  size="small"
                  type="border"
                  color="warning"
                  @click="restoreAllAddCosts">
                  Restore All
                </vs-button>
              </div>
              <vs-table :data="removed_add_costs" class="table-dark-inverted">
                <template slot="thead">
                  <vs-th>NAME / DESCRIPTION</vs-th>
                  <vs-th>NOMINAL CODE</vs-th>
                  <vs-th>AMOUNT (EXCL VAT)</vs-th>
                  <vs-th>VAT (%)</vs-th>
                  <vs-th class="text-center">ACTION</vs-th>
                </template>
                <template slot-scope="{data}">
                  <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                    <vs-td>
                      <span class="font-semibold">{{ tr.name }}</span>
                    </vs-td>
                    <vs-td>
                      {{ getNominalCodeDisplay(tr) }}
                    </vs-td>
                    <vs-td>
                      {{ tr.amount }}
                    </vs-td>
                    <vs-td>
                      {{ tr.vat_percent }}%
                    </vs-td>
                    <vs-td class="text-center">
                      <vx-tooltip text="Restore cost to order" position="top">
                        <vs-button
                          size="small"
                          color="primary"
                          type="border"
                          icon-pack="feather"
                          icon="icon-plus"
                          @click="restoreAddCost(indextr)">
                          Add Back
                        </vs-button>
                      </vx-tooltip>
                    </vs-td>
                  </vs-tr>
                </template>
              </vs-table>
            </div>
          </div>

          <!-- Save & Cancel Button -->
          <div class="flex flex-wrap items-center justify-end mt-6">
            <vs-button button="submit" class="ml-auto" color="primary">Clone Order</vs-button>
            <vs-button @click="open = false" class="ml-4" type="border" color="danger">Cancel</vs-button>
          </div>
        </form>
      </ValidationObserver>
    </vx-card>
  </vs-popup>
</template>

<script>
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import SuccessMessage from '@/mixins/SuccessMessage'
import ErrorMessages from '@/mixins/ErrorMessages'

const CustomerRepository = RepositoryFactory.get('customer')

function initialState() {
  return {
    open: false,
    orderId: '',
    product_order_code: '',
    dateConfig: {
      dateFormat: 'd/m/Y'
    },
    form: {
      customers_order_number: '',
      formulation_items: [],
      add_costs: []
    },
    removed_items: [],
    removed_add_costs: []
  }
}

export default {
  name: 'CloneProductOrderPopUp',
  components: {
    flatPickr
  },
  mixins: [SuccessMessage, ErrorMessages],
  data() {
    return initialState()
  },
  methods: {
    openPopup(order) {
      Object.assign(this.$data, initialState())
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
      this.orderId = order.id
      this.product_order_code = order.product_order_code || ''
      this.form.customers_order_number = order.customers_order_number || ''
      this.form.formulation_items = (order.formulation_items || []).map(item => ({
        id: item.id,
        formulation_code: item.formulation_code || '',
        formulation_name: item.formulation_name || '',
        customer_item_code: item.customer_item_code || '',
        customer_item_name: item.customer_item_name || '',
        quantity: item.quantity,
        pack_size: item.pack_size,
        item_status: item.item_status || (item.status && item.status.name) || '',
        required_by: '',
        shipment_date: ''
      }))
      this.form.add_costs = (order.add_costs || []).map(cost => ({
        name: cost.name || '',
        amount: cost.amount != null && !isNaN(Number(cost.amount)) ? Number(cost.amount).toFixed(2) : (cost.amount || '0.00'),
        vat_percent: cost.vat_percent != null && !isNaN(Number(cost.vat_percent)) ? Number(cost.vat_percent).toFixed(2) : (cost.vat_percent || '0.00'),
        sage_nominal_code_id: cost.sage_nominal_code_id != null ? cost.sage_nominal_code_id : (cost.sage_nominal_code && cost.sage_nominal_code.id ? cost.sage_nominal_code.id : null),
        sage_nominal_code: cost.sage_nominal_code || null
      }))
      this.open = true
    },
    removeItem(index) {
      const item = this.form.formulation_items.splice(index, 1)[0]
      if (item) {
        this.removed_items.push(item)
      }
    },
    restoreItem(index) {
      const item = this.removed_items.splice(index, 1)[0]
      if (item) {
        this.form.formulation_items.push(item)
      }
    },
    restoreAllItems() {
      this.form.formulation_items.push(...this.removed_items)
      this.removed_items = []
    },
    removeAddCost(index) {
      const item = this.form.add_costs.splice(index, 1)[0]
      if (item) {
        this.removed_add_costs.push(item)
      }
    },
    restoreAddCost(index) {
      const item = this.removed_add_costs.splice(index, 1)[0]
      if (item) {
        this.form.add_costs.push(item)
      }
    },
    restoreAllAddCosts() {
      this.form.add_costs.push(...this.removed_add_costs)
      this.removed_add_costs = []
    },
    calculateTotalWithVat(amount, vatPercent) {
      const amt = Number(amount) || 0
      const vat = Number(vatPercent) || 0
      return (amt * (1 + vat / 100)).toFixed(2)
    },
    getNominalCodeDisplay(item) {
      if (item.sage_nominal_code) {
        if (typeof item.sage_nominal_code === 'object') {
          return item.sage_nominal_code.nominal_code || item.sage_nominal_code.code || item.sage_nominal_code.name || item.sage_nominal_code_id || '-'
        }
        return item.sage_nominal_code
      }
      return item.sage_nominal_code_id || '-'
    },
    itemStatusColorFactory(status) {
      if (!status) return 'dark'
      switch (status.toLowerCase()) {
        case 'submitted':
          return 'warning'
        case 'pending':
        case 'accepted':
          return 'success'
        case 'in-manufacturing':
        case 'in_manufacturing':
          return 'primary'
        case 'in-dispatch':
        case 'in_dispatch':
        case 'dispatched':
          return 'primary'
        case 'customer-received':
        case 'customer_received':
          return 'success'
        case 'cancelled':
        case 'rejected':
          return 'danger'
        case 'returned':
        case 'part returned':
          return 'danger'
        default:
          return 'dark'
      }
    },
    async onClickCloneOrder() {
      if (!this.form.formulation_items || this.form.formulation_items.length === 0) {
        this.$vs.notify({
          title: 'Validation Error',
          text: 'Please include at least one item to clone the order.',
          color: 'warning',
          icon: 'icon-alert-triangle',
          iconPack: 'feather'
        })
        return
      }
      this.$vs.loading({
        container: '#clone-product-order-popup',
        scale: 0.45
      })
      try {
        const payload = {
          customers_order_number: this.form.customers_order_number,
          formulation_items: this.form.formulation_items.map(item => ({
            id: item.id,
            required_by: item.required_by,
            shipment_date: item.shipment_date
          })),
          add_costs: (this.form.add_costs || []).map(cost => ({
            name: cost.name,
            amount: cost.amount,
            vat_percent: cost.vat_percent,
            sage_nominal_code_id: cost.sage_nominal_code_id
          }))
        }
        const res = await CustomerRepository.cloneCustomerProductOrder(this.orderId, payload)
        this.showSuccessMessage('Sales order cloned successfully!')
        this.open = false
        const newOrderId = (res && res.data && res.data.id) || (res && res.data && res.data.data && res.data.data.id)
        if (newOrderId) {
          this.$router.push({ path: `/pages/product-orders/${newOrderId}` })
        } else {
          this.$emit('refresh')
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.$vs.loading.close('#clone-product-order-popup > .con-vs-loading')
      }
    }
  }
}
</script>

<style scoped>
.clone-product-order-popup >>> .vs-popup {
  min-width: 60% !important;
}
</style>
<style>
.flatpickr-calendar {
  z-index: 110000 !important;
}
</style>
